import React, { useState } from 'react';
import { Button, Input, Dropdown, Message, Loader, Label, Icon } from 'semantic-ui-react';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { PageSection } from 'common/styles/page';
import { useGetEmailLogsQuery } from '../../../../store/generated/admin/messaging-monitoring';
import { format } from 'date-fns';

interface EmailLog {
  id: number;
  email: string;
  sent_date: string;
  response: string;
  template: string;
  subject: string;
  template_id: string;
  template_name: string;
  postmark_message_id: string;
  postmark_submitted_at: string;
  postmark_delivered_at: string;
  postmark_bounced_at: string;
  postmark_complained_at: string;
  status: string;
  error_message: string;
  retry_count: number;
  context_data: any;
  created_at: string;
  updated_at: string;
  content_type_name: string;
  object_id: number;
  is_successful: boolean;
  is_failed: boolean;
}

interface EmailLogsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: EmailLog[];
}

const EmailLogsViewer: React.FC = () => {
  const [filters, setFilters] = useState({
    status: 'all',
    template: '',
    email: '',
    days: 7
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const {
    data: emailLogsData,
    error,
    isLoading,
    refetch
  } = useGetEmailLogsQuery({
    status: filters.status !== 'all' ? filters.status : undefined,
    template: filters.template || undefined,
    email: filters.email || undefined,
    days: filters.days,
    page,
    page_size: pageSize
  });

  const emailLogs = emailLogsData as EmailLogsResponse;

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const handleRefresh = () => {
    refetch();
  };

  const getStatusLabelColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'green';
      case 'sent':
        return 'blue';
      case 'bounced':
        return 'red';
      case 'complained':
        return 'orange';
      case 'failed':
        return 'red';
      case 'pending':
        return 'grey';
      default:
        return 'grey';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  if (isLoading) {
    return (
      <PageSection>
        <Loader active size="large" />
      </PageSection>
    );
  }

  if (error) {
    return (
      <PageSection>
        <Message negative>
          <Message.Header>Error Loading Data</Message.Header>
          <p>Failed to load email logs. Please try again.</p>
        </Message>
      </PageSection>
    );
  }

  return (
    <PageSection>
      {/* Filters */}
      <PageSection padbottom display='flex' justifyContent='space-between'>
        <PageSection display='flex' gap="10px" flexDirection='row'>
          <Dropdown
            selection
            value={filters.status}
            onChange={(e, { value }) => handleFilterChange('status', value as string)}
            options={[
              { key: 'all', text: 'All Statuses', value: 'all' },
              { key: 'pending', text: 'Pending', value: 'pending' },
              { key: 'sent', text: 'Sent', value: 'sent' },
              { key: 'delivered', text: 'Delivered', value: 'delivered' },
              { key: 'bounced', text: 'Bounced', value: 'bounced' },
              { key: 'complained', text: 'Complained', value: 'complained' },
              { key: 'failed', text: 'Failed', value: 'failed' },
            ]}
          />
          <Dropdown
            selection
            value={filters.days}
            onChange={(e, { value }) => handleFilterChange('days', value as string)}
            options={[
              { key: 1, text: 'Last 24 hours', value: 1 },
              { key: 7, text: 'Last 7 days', value: 7 },
              { key: 30, text: 'Last 30 days', value: 30 },
              { key: 90, text: 'Last 90 days', value: 90 },
            ]}
          />
        </PageSection>
        <PageSection display='flex' gap="10px" flexDirection='row'>
          <Input
            type="text"
            value={filters.template}
            onChange={(e) => handleFilterChange('template', e.target.value)}
            placeholder="Filter by Template"
          />
          <Input
            type="text"
            value={filters.email}
            onChange={(e) => handleFilterChange('email', e.target.value)}
            placeholder="Filter by Email"
          />
          <Button
            icon
            title="Refresh"
            aria-label="Refresh"
            onClick={handleRefresh}
            size="small"
          >
            <Icon name="refresh" />
          </Button>
        </PageSection>
      </PageSection>

      {emailLogs?.results?.length > 0 && (
        <>
          {/* Email Logs Table */}
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <HeaderCell>Email</HeaderCell>
                  <HeaderCell>ID</HeaderCell>
                  <HeaderCell>Template</HeaderCell>
                  <HeaderCell>Subject</HeaderCell>
                  <HeaderCell>Status</HeaderCell>
                  <HeaderCell>Sent Date</HeaderCell>
                  <HeaderCell>Error Message</HeaderCell>
                  <HeaderCell>Retry Count</HeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emailLogs?.results?.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell $firstcol style={{ fontWeight: '500' }}>{log.email}</TableCell>
                    <TableCell style={{ fontFamily: 'monospace', fontSize: '12px' }}>{log.id}</TableCell>
                    <TableCell title={log.template}>
                      {truncateText(log.template || 'Unknown', 30)}
                    </TableCell>
                    <TableCell title={log.subject}>
                      {truncateText(log.subject || 'No subject', 40)}
                    </TableCell>
                    <TableCell>
                      <Label color={getStatusLabelColor(log.status)}>
                        {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                      </Label>
                    </TableCell>
                    <TableCell style={{ fontSize: '12px' }}>
                      {formatDate(log.sent_date)}
                    </TableCell>
                    <TableCell title={log.error_message} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {truncateText(log.error_message || 'No error', 30)}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                      {log.retry_count > 0 ? (
                        <Label color="orange">{log.retry_count}</Label>
                      ) : (
                        <span style={{ color: '#999' }}>0</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {emailLogs && emailLogs.count > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
              <Button
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
                size="small"
              >
                Previous
              </Button>
              <span style={{ fontSize: '14px', color: '#666' }}>
                Page {page} of {Math.ceil(emailLogs.count / pageSize)}
              </span>
              <Button
                onClick={() => setPage(prev => prev + 1)}
                disabled={!emailLogs.next}
                size="small"
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* No Results */}
      {emailLogs?.results?.length === 0 && (
        <Message info>
          No email logs found matching the current filters.
        </Message>
      )}
    </PageSection>
  );
};

export default EmailLogsViewer;
